"use client";

/**
 * Mobile Bottom Navigation Drawer
 *
 * Navigation-specific implementation using the Unified Drawer System.
 * Automatically optimizes for mobile with intelligent routing and stack management.
 * For comprehensive drawer usage guide, examples, and best practices, see:
 * 📖 docs/ui-components/UNIFIED_DRAWER_SYSTEM.md
 */

import React from "react";
import {
  CentralizedDrawer,
  useGlobalDrawerStack,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@wtc/ui";

// Import extracted types
import { BreadcrumbItem, MobileBottomDrawerProps } from "./mobile-drawer-types";
import { NavigationItem } from "./types";

// Import extracted hooks
import {
  useMobileDrawerNavigation,
  useMobileDrawerAuth,
  useMobileDrawerSearch,
} from "./hooks";

// Import extracted utilities
import { groupNavigationItems } from "./utils/navigation-grouping";

// Import extracted components
import {
  MobileDrawerHeader,
  MobileDrawerSearchWithTabs,
  MobileDrawerNavigationItems,
  MobileDrawerActions,
} from "./components";

interface MobileDrawerSearchWrapperProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  isClientUser: boolean;
  navigationStack: BreadcrumbItem[];
  currentLevel: BreadcrumbItem;
  onNavigateBack: () => void;
  onNavigateToRoot: () => void;
  onNavigateToLevel: (levelIndex: number) => void;
  onItemClick: (item: NavigationItem) => void;
  closeDrawer: (drawerId: string) => void;
  pathname: string;
}

// Separate component for search logic (own-infra CRM + email search).
// Rendered inside the drawer so the search hook mounts only when the drawer opens.
function MobileDrawerSearchWrapper({
  isOpen,
  onOpenChange,
  isClientUser,
  navigationStack,
  currentLevel,
  onNavigateBack,
  onNavigateToRoot,
  onNavigateToLevel,
  onItemClick,
  closeDrawer,
  pathname,
}: MobileDrawerSearchWrapperProps) {
  // Hook is called inside a component that's rendered after the provider wraps it
  const search = useMobileDrawerSearch({
    isOpen,
    onOpenChange,
    showFullPageLink: true,
    fullPageSearchUrl: "/search",
  });

  return (
    <>
      {/* Navigation Header — pill + controls in one unified block */}
      <div className="px-4 pt-2 pb-3">
        {/* Swipe handle pill */}
        <div className="flex justify-center mb-2">
          <div className="w-10 h-1 rounded-full bg-gray-300" />
        </div>
        <MobileDrawerHeader
          isSearchMode={search.isSearchMode}
          canSearch={!isClientUser}
          navigationStack={navigationStack}
          currentLevel={currentLevel}
          searchQuery={search.searchQuery}
          searchResults={search.searchResults}
          onSearchToggle={search.handleSearchToggle}
          onNavigateBack={onNavigateBack}
          onNavigateToRoot={onNavigateToRoot}
          onNavigateToLevel={onNavigateToLevel}
          onClearSearch={search.handleClearSearch}
          onClose={() => closeDrawer("mobile-navigation")}
        />
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-4">
        {!isClientUser && search.isSearchMode ? (
          <MobileDrawerSearchWithTabs
            activeTab={search.activeTab}
            setActiveTab={search.setActiveTab}
            searchResults={search.searchResults}
            renderHighlightedText={search.renderHighlightedText}
            getResultIcon={search.getResultIcon}
            onResultClick={search.handleSearchResultClick}
            historyItems={search.historyItems}
            onHistoryItemClick={search.handleHistoryItemClick}
            onRemoveHistoryItem={search.handleRemoveHistoryItem}
            showHistoryDrawer={true}
            showFullPageLink={search.showFullPageLink}
            fullPageSearchUrl={search.fullPageSearchUrl}
            onClose={search.handleClose}
            searchQuery={search.searchQuery}
            onSearchChange={search.handleSearchChange}
            isSearchLoading={search.isSearchLoading}
          />
        ) : (
          <MobileDrawerNavigationItems
            navigationStack={navigationStack}
            currentLevel={currentLevel}
            groupedItems={groupNavigationItems(currentLevel.items)}
            pathname={pathname}
            onItemClick={onItemClick}
            onClose={() => closeDrawer("mobile-navigation")}
          />
        )}
      </div>
    </>
  );
}

export function MobileBottomDrawer({
  items,
  pathname,
  trigger,
  tooltipText,
}: Omit<MobileBottomDrawerProps, "isOpen" | "onOpenChange">) {
  const { openDrawer, closeDrawer, isDrawerOpen } = useGlobalDrawerStack();

  // Use global drawer stack for mobile navigation
  // This ensures the drawer state is properly managed by the drawer stack
  const isOpen = isDrawerOpen("mobile-navigation");
  const handleOpenChange = (open: boolean) => {
    if (open) {
      openDrawer("mobile-navigation");
    } else {
      // Close drawer via drawer stack
      closeDrawer("mobile-navigation");
    }
  };

  // Use extracted hooks with drawer stack state
  const navigation = useMobileDrawerNavigation({ items, isOpen, onOpenChange: handleOpenChange });
  const auth = useMobileDrawerAuth({ onOpenChange: handleOpenChange });

  // Clone trigger with onClick handler if it's a React element
  const triggerWithHandler =
    trigger && React.isValidElement(trigger)
      ? React.cloneElement(trigger as React.ReactElement<any>, {
          onClick: (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            openDrawer("mobile-navigation");
          },
        })
      : trigger;

  return (
    <>
      {tooltipText ? (
        <Tooltip>
          <TooltipTrigger asChild>{triggerWithHandler}</TooltipTrigger>
          <TooltipContent>{tooltipText}</TooltipContent>
        </Tooltip>
      ) : (
        triggerWithHandler
      )}
      <CentralizedDrawer
        drawerId="mobile-navigation"
        side="bottom"
        showCloseButton={false}
      >
        {/* Search wrapper — full width, manages its own header, user info, and nav content */}
        <MobileDrawerSearchWrapper
          isOpen={isOpen}
          onOpenChange={handleOpenChange}
          isClientUser={auth.isClientUser}
          navigationStack={navigation.navigationStack}
          currentLevel={navigation.currentLevel}
          onNavigateBack={navigation.navigateBack}
          onNavigateToRoot={navigation.navigateToRoot}
          onNavigateToLevel={navigation.navigateToLevel}
          onItemClick={navigation.handleItemClick}
          closeDrawer={closeDrawer}
          pathname={pathname}
        />

        {/* pb clears the mobile home indicator / rounded corners via safe-area inset. */}
        <div className="border-t p-4 pb-[calc(2rem+env(safe-area-inset-bottom))]">
          <MobileDrawerActions
            canAccessCRM={auth.canAccessCRM}
            isFeatureEnabled={auth.isFeatureEnabled}
            onClose={() => closeDrawer("mobile-navigation")}
            onSignOut={auth.handleSignOut}
            appUser={auth.appUser}
            getUserTypeDisplay={auth.getUserTypeDisplay}
          />
        </div>
      </CentralizedDrawer>
    </>
  );
}
